import { DERIV_REST_BASE_URL, getPatAppId } from './deriv-config';
import { getSession, saveSession } from './storage';

// Version bump discards entries produced by the old collision-prone token key.
const CACHE_KEY = 'commission_dashboard.commission_cache_v2';
const CACHE_MAX_ENTRIES = 500;
const OPEN_RANGE_TTL = 60_000; // a range that includes today is still moving
const REQUEST_INTERVAL = 1500;
const CHUNK_SIZE = 15;
const CHUNK_REST = 10_000;
const RATE_LIMIT_COOLDOWN = 10 * 60_000;
const CMS_RUNTIME_URL = (process.env.REACT_APP_CREATEMYSITE_RUNTIME_URL || 'https://runtime.createmysite.dev').replace(/\/+$/, '');
const isCreateMySiteToken = (token) => String(token || '').trim().startsWith('cms_');

// Single source of truth for the active session + request queue.
const expiredTokens = new Set();
export const SESSION_EXPIRED_EVENT = 'commission:session-expired';
const tokenExpiredError = () => Object.assign(new Error('Token expired or invalid. Please sign in again.'), { status: 401 });

const session = {
  token: null,
  authorized: false,
  appList: [],
  pending: new Map(),
  queue: Promise.resolve(),
  lastRequest: 0,
  requestCount: 0,
  rateLimitUntil: 0,
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const getTodayDate = () => new Date().toISOString().slice(0, 10);
const getYesterdayDate = () => {
  const date = new Date();
  date.setUTCDate(date.getUTCDate() - 1);
  return date.toISOString().slice(0, 10);
};
// A suffix is not a unique token identity and can return another token's cache.
const getTokenCacheKey = (token) => encodeURIComponent(String(token || ''));

const getDate = (value) => {
  const match = String(value || '').match(/^\d{4}-\d{2}-\d{2}/);
  if (!match) throw new Error('Commission dates must start with YYYY-MM-DD.');
  return match[0];
};

// ─── Persistent cache ────────────────────────────────────────────────────────
// The markup-statistics endpoint has no batch/group-by mode — a chart needs
// one request per day or per month it plots. A range that ends before today
// is closed and can never change, so it's cached indefinitely; a range still
// touching today gets a short TTL. Storing this in localStorage (not memory)
// means a page refresh doesn't re-fire every one of those requests.

const readCacheStore = () => {
  try {
    return JSON.parse(window.localStorage.getItem(CACHE_KEY)) || {};
  } catch {
    return {};
  }
};

const writeCacheStore = (store) => {
  try {
    const entries = Object.entries(store);
    const pruned = entries.length > CACHE_MAX_ENTRIES
      ? Object.fromEntries(entries.sort((a, b) => b[1].ts - a[1].ts).slice(0, CACHE_MAX_ENTRIES))
      : store;
    window.localStorage.setItem(CACHE_KEY, JSON.stringify(pruned));
  } catch {
    // Storage full or unavailable — cache just won't survive a reload.
  }
};

const getCached = (key, isClosedRange) => {
  const entry = readCacheStore()[key];
  if (!entry) return null;
  if (isClosedRange) return entry.data;
  return Date.now() - entry.ts < OPEN_RANGE_TTL ? entry.data : null;
};

const setCached = (key, data) => {
  const store = readCacheStore();
  store[key] = { data, ts: Date.now() };
  writeCacheStore(store);
};

// ─── REST request ────────────────────────────────────────────────────────────

const getRestErrorMessage = (body, response) =>
  body?.errors?.[0]?.message ||
  body?.error?.message ||
  (typeof body?.error === 'string' ? body.error : null) ||
  `Deriv API request failed with HTTP ${response.status}.`;

const getNumeric = (value) => Number(value || 0);

const normalize = (body) => {
  const data = body?.data?.data || body?.data || body || {};
  const breakdown = Array.isArray(data.breakdown)
    ? data.breakdown.map((app) => ({
        ...app,
        app_markup_usd: getNumeric(app.app_markup_usd),
        volume_usd: getNumeric(app.volume_usd),
        payout_usd: getNumeric(app.payout_usd),
        contract_count: getNumeric(app.contract_count ?? app.transactions_count),
        transactions_count: getNumeric(app.transactions_count ?? app.contract_count),
        client_count: getNumeric(app.client_count),
      }))
    : [];
  return {
    app_markup_statistics: {
      ...data,
      breakdown,
      total_app_markup_usd: getNumeric(data.total_app_markup_usd),
      total_volume_usd: getNumeric(data.total_volume_usd),
      total_payout_usd: getNumeric(data.total_payout_usd),
      total_contract_count: getNumeric(data.total_contract_count ?? data.total_transactions_count),
      total_transactions_count: getNumeric(data.total_transactions_count ?? data.total_contract_count),
      total_client_count: getNumeric(data.total_client_count),
    },
    meta: body?.data?.meta || body?.meta,
  };
};

const createRateLimitError = () => {
  const err = new Error('Deriv API rate limit reached. Please wait a few minutes before trying again.');
  err.rateLimited = true;
  return err;
};

// fetch() throws a generic "Failed to fetch" TypeError (no readable status) when Deriv's
// edge rate-limits us — those responses carry no CORS headers, so the browser can't see the
// 429 underneath. Retrying into this just extends the block (Retry-After climbs from 10s to
// 500s+ under repeated hits), so surface it immediately instead of retrying.
const performRequest = async (token, appId, dateFrom, dateTo) => {
  const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
  let res;
  try {
    const sharedAccess = isCreateMySiteToken(token);
    const endpoint = sharedAccess
      ? `${CMS_RUNTIME_URL}/api/commission/shared/statistics?${params}`
      : `${DERIV_REST_BASE_URL}/applications/v1/markup-statistics?${params}`;
    res = await fetch(endpoint, {
      headers: {
        Authorization: `Bearer ${token}`,
        ...(sharedAccess ? {} : { 'Deriv-App-ID': appId }),
      },
    });
  } catch {
    throw createRateLimitError();
  }

  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (res.status === 401) {
      expiredTokens.add(token);
      if (session.token === token) {
        session.authorized = false;
        window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
      }
      throw tokenExpiredError();
    }
    throw Object.assign(new Error(getRestErrorMessage(body, res)), { status: res.status });
  }
  return body;
};

export const isRateLimited = () => Date.now() < session.rateLimitUntil;
export const getRateLimitRetryAt = () => session.rateLimitUntil;

// Rate-limited, cached, deduplicated request queue — every commission fetch
// in the app (dashboard, all-apps, admin, updated-tokens) funnels through here.
const fetchCommission = async (token, appId, from, to) => {
  if (!appId && !isCreateMySiteToken(token)) {
    throw new Error('Deriv-App-ID is missing for API requests.');
  }

  if (expiredTokens.has(token)) throw tokenExpiredError();
  const dateFrom = getDate(from);
  const dateTo = getDate(to);
  const cacheKey = `${getTokenCacheKey(token)}:${appId}:${dateFrom}:${dateTo}`;
  const isClosedRange = dateTo < getTodayDate();

  const cached = getCached(cacheKey, isClosedRange);
  if (cached) return cached;
  if (session.pending.has(cacheKey)) return session.pending.get(cacheKey);

  const req = session.queue
    .then(async () => {
      // Circuit breaker: a prior block means Deriv's edge is still likely to reject us —
      // refuse locally instead of hammering it again and extending the cooldown further.
      if (expiredTokens.has(token)) throw tokenExpiredError();
      if (isRateLimited()) throw createRateLimitError();

      const waitFor = session.lastRequest + REQUEST_INTERVAL - Date.now();
      if (waitFor > 0) await wait(waitFor);
      session.requestCount += 1;
      if (session.requestCount % CHUNK_SIZE === 0) await wait(CHUNK_REST);
      session.lastRequest = Date.now();

      let body;
      try {
        body = await performRequest(token, appId, dateFrom, dateTo);
      } catch (err) {
        if (err.rateLimited) session.rateLimitUntil = Date.now() + RATE_LIMIT_COOLDOWN;
        throw err;
      }

      const normalized = normalize(body);
      if (process.env.NODE_ENV === 'development') {
        window.__lastDerivMarkupStatistics = {
          request: { appId, date_from: dateFrom, date_to: dateTo },
          raw: body,
          normalized,
        };
      }
      setCached(cacheKey, normalized);
      return normalized;
    })
    .finally(() => session.pending.delete(cacheKey));

  session.queue = req.catch(() => {});
  session.pending.set(cacheKey, req);
  return req;
};

// ─── Session management ──────────────────────────────────────────────────────

let _connectPromise = null;

/** Validates a token against the API and, on success, makes it the active session. */
export const connectSession = (token) => {
  // fetchCommission already dedupes identical in-flight requests via session.pending,
  // but we dedupe at this level too so session.authorized isn't set twice concurrently.
  if (_connectPromise) return _connectPromise;

  _connectPromise = (async () => {
    if (isCreateMySiteToken(token)) {
      const response = await fetch(`${CMS_RUNTIME_URL}/api/commission/shared/session`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error || 'This CreateMySite access token is invalid.');
      session.token = token;
      session.authorized = true;
      session.appList = Array.isArray(result.app_list) ? result.app_list : [];
      return result;
    }
    // Deriv no longer exposes in-progress commission statistics for today.
    // Validate against the latest completed day instead.
    const yesterday = getYesterdayDate();
    const result = await fetchCommission(token, getPatAppId(), yesterday, yesterday);

    session.token = token;
    session.authorized = true;
    session.appList = (result.app_markup_statistics?.breakdown || []).map((a) => ({
      app_id: a.app_id,
    }));

    return result;
  })().finally(() => {
    _connectPromise = null;
  });

  return _connectPromise;
};

export const persistSession = (token) => saveSession({ token });

// ─── Admin / per-token fetchers (bypass session, used by UpdatedTokens) ───

/** Commission for any bearer token — stateless, no active session needed. */
export const fetchCommissionForToken = (token, from, to) => fetchCommission(token, getPatAppId(), from, to);

// ─── Public commission API (uses the active session) ────────────────────────

export const getCommission = async (from, to) => {
  if (!session.authorized || !session.token) {
    throw new Error('No API session connected');
  }
  const result = await fetchCommission(session.token, getPatAppId(), from, to);
  session.appList = (result.app_markup_statistics?.breakdown || []).map((a) => ({
    app_id: a.app_id,
  }));
  return result;
};

export const getAppList = async () => ({ app_list: session.appList });

export const isConnected = () => session.authorized && !!session.token;

export const disconnectDeriv = () => {
  session.token = null;
  session.authorized = false;
  session.appList = [];
  _connectPromise = null;
};

export const hasStoredSession = () => !!getSession()?.token;

/**
 * Session restore on boot. Trusts the saved token without a network round trip —
 * App.js races this against a hard timeout, and any invalid/expired token simply
 * surfaces as a failed request the first time the dashboard actually fetches data.
 */
export const connectDeriv = async (token) => {
  if (!token) throw new Error('No saved token to restore.');
  session.token = token;
  session.authorized = true;
};
